import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from './Navbar';
import Footer from './Footer';

const FilosofiaPrinconserPage: React.FC = () => {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const [activeSection, setActiveSection] = useState<number>(1);

    const t = (es: string, en: string) => language === 'es' ? es : en;

    const handleThemeChange = (theme: number) => {
        const bookRoutes = {
            2: '/libros/1',
            3: '/libros/2', 
            4: '/libros/3',
            5: '/libros/4',
            6: '/libros/5',
            7: '/libros/6',
            8: '/libros/7',
            9: '/libros/8'
        };
        const route = bookRoutes[theme as keyof typeof bookRoutes];
        if (route) {
            window.location.href = route;
        }
    };

    const sections = [
        {
            id: 1,
            title: t('Filosofía Princonser', 'Princonser Philosophy'),
            icon: '🔍',
            color: 'from-blue-500 to-cyan-600',
            content: {
                subtitle: t('La brújula que transforma el aprendizaje en pasión, propósito y poder personal.', 'The compass that transforms learning into passion, purpose, and personal power.'),
                description: t(
                    'Cada lección invita al niño a cuestionar, descubrir y validar por sí mismo, fomentando una curiosidad auténtica y un pensamiento crítico desde temprana edad.',
                    'Each lesson invites the child to question, discover, and validate for themselves, fostering authentic curiosity and critical thinking from an early age.'
                ),
                points: [
                    t('Preguntas que despiertan la curiosidad natural', 'Questions that awaken natural curiosity'),
                    t('Experimentos que validan hipótesis', 'Experiments that validate hypotheses'),
                    t('Reflexión personal sobre cada descubrimiento', 'Personal reflection on each discovery'),
                    t('Desarrollo del escepticismo saludable', 'Development of healthy skepticism')
                ]
            }
        },
        {
            id: 2,
            title: t('Ética en Acción', 'Ethics in Action'),
            icon: '⚖️',
            color: 'from-green-500 to-emerald-600',
            content: {
                subtitle: t('No basta con saber; hay que hacer el bien.', 'It is not enough to know; you must do good.'),
                description: t(
                    'Princonser integra valores universales en cada cuento y ejercicio para construir carácter.',
                    'Princonser integrates universal values in every story and exercise to build character.'
                ),
                points: [
                    t('Decisiones éticas en situaciones reales', 'Ethical decisions in real situations'),
                    t('Responsabilidad por las consecuencias', 'Responsibility for consequences'),
                    t('Empatía y compasión práctica', 'Practical empathy and compassion'),
                    t('Integridad como base del carácter', 'Integrity as the foundation of character')
                ]
            }
        },
        {
            id: 3,
            title: t('Pensamiento Dialéctico', 'Dialectical Thinking'),
            icon: '🔄',
            color: 'from-purple-500 to-pink-600',
            content: {
                subtitle: t('Enseña a identificar lo que conviene conservar y lo que hay que transformar, desarrollando resiliencia y adaptabilidad.', 'Teaches to identify what should be preserved and what needs to be transformed, developing resilience and adaptability.'),
                description: t(
                    '',
                    ''
                ),
                points: [
                    t('Análisis de opuestos y contradicciones', 'Analysis of opposites and contradictions'),
                    t('Síntesis creativa de ideas', 'Creative synthesis of ideas'),
                    t('Adaptabilidad ante el cambio', 'Adaptability to change'),
                    t('Resolución de conflictos constructiva', 'Constructive conflict resolution')
                ]
            }
        },
        {
            id: 4,
            title: t('Cuatro Áreas, Un Todo', 'Four Areas, One Whole'),
            icon: '🎯',
            color: 'from-orange-500 to-red-600',
            content: {
                subtitle: t('Salud Mental-Emocional, Social-Profesional, Biológica-Física y Financiera: un modelo holístico que garantiza un desarrollo equilibrado.', 'Mental-Emotional, Social-Professional, Biological-Physical, and Financial Health: a holistic model that ensures balanced development.'),
                description: t(
                    '',
                    ''
                ),
                points: [
                    t('Equilibrio entre mente y cuerpo', 'Balance between mind and body'),
                    t('Habilidades sociales y profesionales', 'Social and professional skills'),
                    t('Bienestar emocional y mental', 'Emotional and mental well-being'),
                    t('Inteligencia financiera desde la infancia', 'Financial intelligence from childhood')
                ]
            }
        },
        {
            id: 5,
            title: t('Propósito y Significado', 'Purpose and Meaning'),
            icon: '🌟',
            color: 'from-yellow-500 to-amber-600',
            content: {
                subtitle: t('Cada actividad conecta con un valor universal', 'Every activity connects with a universal value'),
                description: t(
                    'Cada lección y actividad tiene un propósito claro que conecta con valores universales, permitiendo que los niños encuentren significado en su aprendizaje y desarrollen un sentido de propósito.',
                    'Each lesson and activity has a clear purpose that connects with universal values, allowing children to find meaning in their learning and develop a sense of purpose.'
                ),
                points: [
                    t('Conexión con valores universales', 'Connection with universal values'),
                    t('Propósito personal y colectivo', 'Personal and collective purpose'),
                    t('Impacto positivo en la comunidad', 'Positive impact on the community'),
                    t('Legado y contribución al mundo', 'Legacy and contribution to the world')
                ]
            }
        }
    ];

    const currentSection = sections.find(s => s.id === activeSection);

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
            {/* Navbar */}
            <Navbar currentTheme={1} onThemeChange={handleThemeChange} />
            
            {/* Contenido principal */}
            <main className="pt-16">
                {/* Contenido de la sección activa */}
                {currentSection && (
                    <section className="py-16 lg:py-24">
                        {activeSection === 5 ? (
                            // Para la sección 5, mostrar solo una imagen que abarque todo el espacio
                            <div className="w-full h-[120vh] relative overflow-hidden flex justify-center">
                                <div className="w-4/5 h-full relative">
                                    <img 
                                        src="/images/infosection/filosofiaprinconser.png"
                                        alt="Filosofía Princonser - Imagen completa"
                                        className="w-full h-full object-contain object-center"
                                    />
                                    {/* Overlay sutil para mejorar legibilidad si es necesario */}
                                    <div className="absolute inset-0 bg-black/5"></div>
                                </div>
                            </div>
                        ) : (
                            // Para las otras secciones, mostrar el contenido normal
                            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                                    {/* Columna izquierda: Contenido */}
                                    <div className="space-y-8">
                                        <div className="text-center lg:text-left">
                                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                                                {currentSection.title}
                                            </h2>
                                            <h3 className="text-xl sm:text-2xl text-gray-600 mb-6">
                                                {currentSection.content.subtitle}
                                            </h3>
                                            {currentSection.content.description && (
                                                <p className="text-lg text-gray-700 leading-relaxed">
                                                    {currentSection.content.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Columna derecha: Imagen o ilustración */}
                                    <div className="relative">
                                        {/* Mostrar las imágenes de osito según la sección */}
                                        <div className="w-full h-96 rounded-2xl bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center shadow-2xl p-8">
                                            <img 
                                                src={`/images/infosection/osito${activeSection}.png`}
                                                alt={`Osito ${activeSection} - ${currentSection.title}`}
                                                className="w-full h-full object-contain"
                                            />
                                        </div>
                                        
                                        {/* Elementos decorativos */}
                                        <div className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-400 rounded-full animate-pulse"></div>
                                        <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-pink-400 rounded-full animate-bounce"></div>
                                        <div className="absolute top-1/2 -left-2 w-4 h-4 bg-green-400 rounded-full animate-pulse"></div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>
                )}

                {/* Navegación entre secciones */}
                <section className="py-8 bg-gray-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between items-center">
                            <button
                                onClick={() => setActiveSection(activeSection > 1 ? activeSection - 1 : 5)}
                                className="flex items-center gap-2 px-6 py-3 bg-white text-gray-700 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
                            >
                                <span>←</span>
                                <span className="hidden sm:inline">{t('Anterior', 'Previous')}</span>
                            </button>
                            
                            <span className="text-gray-600 font-medium">
                                {activeSection} / 5
                            </span>
                            
                            <button
                                onClick={() => setActiveSection(activeSection < 5 ? activeSection + 1 : 1)}
                                className="flex items-center gap-2 px-6 py-3 bg-white text-gray-700 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
                            >
                                <span className="hidden sm:inline">{t('Siguiente', 'Next')}</span>
                                <span>→</span>
                            </button>
                        </div>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default FilosofiaPrinconserPage; 